"use client";

import * as React from "react";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
function MachinePrice({
  product
}: {
  product: any;
}) {
  const regularPrice = typeof product.price === "number" && product.price > 0 ? product.price : null;
  const salePrice = typeof product.discounted_price === "number" && product.discounted_price > 0 && regularPrice !== null && product.discounted_price < regularPrice ? product.discounted_price : null;
  if (!regularPrice) {
    return <div className="text-lg font-bold text-muted-foreground italic">
        Prix sur demande
      </div>;
  }
  if (salePrice) {
    return <div className="flex flex-col gap-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-2xl font-extrabold text-chart-2">
            {salePrice.toLocaleString("fr-FR")} FCFA
          </div>
          <div className="text-sm font-medium text-muted-foreground line-through">
            {regularPrice.toLocaleString("fr-FR")} FCFA
          </div>
        </div>
      </div>;
  }
  return <div className="text-2xl font-extrabold text-chart-2">
      {regularPrice.toLocaleString("fr-FR")} FCFA
    </div>;
}
function MachinesGrid() {
  const {
    posts: products,
    loading,
    error
  } = useEcommerceProducts();
  const visibleProducts = React.useMemo(() => {
    return (products || []).filter(product => !!product.featured_image_url).sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "fr", {
      sensitivity: "base"
    }));
  }, [products]);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden rounded-xl border border-border">
            <AspectRatio ratio={1}>
              <Skeleton className="h-full w-full rounded-none" />
            </AspectRatio>
            <CardContent className="flex flex-col gap-3 p-5">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="mt-4 h-8 w-1/2" />
            </CardContent>
            <CardFooter className="p-5 pt-0">
              <Skeleton className="h-12 w-full" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-destructive">
        <p>Échec du chargement des machines. Veuillez réessayer plus tard.</p>
      </div>;
  }
  if (!visibleProducts.length) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>Aucune machine disponible pour le moment.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {visibleProducts.map((product, i) => {
      const image = product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592750/img/product_placeholder.png";
      const title = String(product.title || "").toUpperCase();
      const brandLabel = title.includes("TASSIMO") ? "Bosch / Tassimo" : "Krups / Dolce Gusto";
      const regularPrice = typeof product.price === "number" && product.price > 0 ? product.price : null;
      const salePrice = typeof product.discounted_price === "number" && product.discounted_price > 0 && regularPrice !== null && product.discounted_price < regularPrice ? product.discounted_price : null;
      const hasDiscount = !!salePrice;
      return <Card key={product.id} data-index={i} className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
            <Link to={product.link || "#"} className="relative block overflow-hidden bg-white">
              <AspectRatio ratio={1}>
                <img src={image} alt={product.title} className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              </AspectRatio>
            </Link>

            <CardContent className="flex flex-grow flex-col gap-2 p-5">
              <Link to={product.link || "#"} className="transition-colors group-hover:text-chart-2">
                <h3 className="line-clamp-2 text-lg font-bold leading-tight text-foreground font-serif" dangerouslySetInnerHTML={{
              __html: product.title
            }} />
              </Link>

              <div className="flex items-center gap-2 pt-1">
                <span className="inline-block rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-foreground">
                  Machine
                </span>

                {hasDiscount && <span className="inline-block rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-600">
                    Promo
                  </span>}
              </div>

              <div className="mt-auto pt-4">
                <span className="mb-1 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {brandLabel}
                </span>
                <MachinePrice product={product} />
              </div>
            </CardContent>

            <CardFooter className="p-5 pt-0">
              <Button asChild className="h-12 w-full rounded-lg bg-chart-2 text-base font-bold text-primary-foreground shadow-sm transition-all hover:bg-chart-2/90">
                <Link to={product.link || "#"}>Voir les détails</Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function MachinesCafe() {
  const wp_query = React.useMemo(() => new WP_Query({
    post_type: "product",
    product_cat: "machines",
    posts_per_page: 12,
    orderby: "title",
    order: "ASC"
  }), []);
  return <section id="machines-cafe" className="w-full bg-background py-16 md:py-20 lg:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center justify-center space-y-4 text-center md:mb-16">
          <span className="text-primary text-sm font-medium uppercase tracking-wider">
            Équipement Premium
          </span>

          <h2 className="font-serif text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-6xl">
            Machines à Café
          </h2>

          <p className="max-w-[800px] font-sans text-muted-foreground md:text-xl/relaxed lg:text-lg/relaxed xl:text-xl/relaxed">
            Découvrez notre sélection exclusive de machines Tassimo et Dolce Gusto
            pour un café d&apos;exception à Dakar et Thiès.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query} postType="product">
          <MachinesGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}